import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ScrollSection } from '../components/ScrollSection';

function DepartmentDetail() {
  const { id } = useParams();
  const [department, setDepartment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDepartment();
  }, [id]);

  const fetchDepartment = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/departments/${id}`);
      setDepartment(response.data);
    } catch (error) {
      console.error('Error fetching department:', error);
      if (error.response?.status === 404) {
        setError('Department not found');
      } else {
        setError('Failed to load department details');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5" style={{ maxWidth: '1200px' }}>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !department) {
    return (
      <div className="container py-5" style={{ maxWidth: '1200px' }}>
        <div className="alert alert-danger">
          <h4>Department Not Found</h4>
          <p>{error || 'The department you are looking for does not exist.'}</p>
          <Link to="/departments-centers" className="btn btn-primary">
            Back to Departments
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Simple Header */}
      <div className="container py-4" style={{ maxWidth: '1200px' }}>
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <Link to="/" className="text-decoration-none" style={{ color: '#0066cc' }}>
                Home
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/departments-centers" className="text-decoration-none" style={{ color: '#0066cc' }}>
                Departments & Centers
              </Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              {department.name}
            </li>
          </ol>
        </nav>
        <h1 className="display-4 mb-0" style={{ fontFamily: 'Georgia, serif', fontWeight: 'bold', color: '#000' }}>
          {department.name}
        </h1>
      </div>

      {/* Main Content */}
      <div className="container py-5" style={{ maxWidth: '1200px' }}>
        {/* Description with Image Wrapping */}
        {department.description && (
          <ScrollSection>
            <div className="mb-5 department-image-wrap">
              {department.image && (
                <img 
                  src={department.image} 
                  alt={department.name}
                />
              )}
              <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#333', textAlign: 'justify' }}>
                {department.description}
              </p>
              <div style={{ clear: 'both' }}></div>
            </div>
          </ScrollSection>
        )}

        {/* Image only if no description */}
        {!department.description && department.image && (
          <ScrollSection>
            <div className="mb-5">
              <img 
                src={department.image} 
                alt={department.name}
                style={{ 
                  width: '100%', 
                  maxWidth: '800px',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  display: 'block',
                  margin: '0 auto'
                }}
              />
            </div>
          </ScrollSection>
        )}

        {/* Vision and Mission */}
        {(department.vision || department.mission) && (
          <div className="row mb-5">
            {department.vision && (
              <div className={department.mission ? 'col-lg-6 mb-4 mb-lg-0' : 'col-12'}>
                <ScrollSection>
                  <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '2rem', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #0066cc',
                    height: '100%'
                  }}>
                    <h2 style={{ 
                      color: '#0066cc', 
                      fontFamily: 'Georgia, serif', 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold',
                      marginBottom: '1rem'
                    }}>
                      Vision
                    </h2>
                    <p style={{ fontSize: '1rem', lineHeight: '1.8', color: '#333', textAlign: 'justify' }}>
                      {department.vision}
                    </p>
                  </div>
                </ScrollSection>
              </div>
            )}
            {department.mission && (
              <div className={department.vision ? 'col-lg-6' : 'col-12'}>
                <ScrollSection>
                  <div style={{ 
                    backgroundColor: '#f8f9fa', 
                    padding: '2rem', 
                    borderRadius: '8px',
                    borderLeft: '4px solid #dc3545',
                    height: '100%'
                  }}>
                    <h2 style={{ 
                      color: '#0066cc', 
                      fontFamily: 'Georgia, serif', 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold',
                      marginBottom: '1rem'
                    }}>
                      Mission
                    </h2>
                    <p style={{ fontSize: '1rem', lineHeight: '1.8', color: '#333', textAlign: 'justify' }}>
                      {department.mission}
                    </p>
                  </div>
                </ScrollSection>
              </div>
            )}
          </div>
        )}

        {/* Units */}
        {department.units && department.units.length > 0 && (
          <ScrollSection>
            <div className="mb-5">
              <h2 style={{ 
                color: '#0066cc', 
                fontFamily: 'Georgia, serif', 
                fontSize: '2rem', 
                fontWeight: 'bold',
                marginBottom: '1.5rem'
              }}>
                Units
              </h2>
              <div className="row g-4">
                {department.units.map((unit) => (
                  <div key={unit.id} className="col-md-6 col-lg-4">
                    <div style={{ 
                      backgroundColor: '#f8f9fa', 
                      padding: '1.5rem', 
                      borderRadius: '8px',
                      borderLeft: '4px solid #0066cc',
                      height: '100%'
                    }}>
                      <h3 style={{ 
                        color: '#0066cc', 
                        fontFamily: 'Georgia, serif', 
                        fontSize: '1.2rem', 
                        fontWeight: 'bold',
                        marginBottom: '0.75rem'
                      }}>
                        {unit.name}
                      </h3>
                      {unit.description && (
                        <p style={{ fontSize: '0.95rem', lineHeight: '1.7', color: '#333' }}>
                          {unit.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollSection>
        )}

        {/* Back to Departments */}
        <ScrollSection>
          <div className="text-center">
            <Link
              to="/departments-centers"
              className="btn"
              style={{
                backgroundColor: '#0066cc',
                color: '#fff',
                border: 'none',
                padding: '0.75rem 2rem',
                borderRadius: '4px',
                textDecoration: 'none',
                display: 'inline-block',
                fontSize: '1rem',
                fontWeight: '500'
              }}
            >
              Back to All Departments
            </Link>
          </div>
        </ScrollSection>
      </div>
    </>
  );
}

export default DepartmentDetail;
